/**
 * Query keys — the contract between the server's SSR data resolver and the client's
 * cache.
 *
 * They live in `packages/shared` rather than in `apps/web` because both sides build
 * them: the API seeds cache entries during SSR (`ssr-data.ts`) and the browser reads
 * them back (`route-data.ts`). A key that differs by one character between the two means
 * the client silently refetches everything the server already sent — a bug with no error
 * message, visible only as a slower page.
 */
export const queryKeys = {
    homepage: (locale) => ['homepage', locale],
    feed: (locale, filters = {}) => ['feed', locale, normalize(filters)],
    article: (locale, path) => ['article', locale, path],
    categories: (locale) => ['categories', locale],
    districts: (locale) => ['districts', locale],
    authors: (locale) => ['authors', locale],
    tag: (locale, slug) => ['tag', locale, slug],
    author: (locale, slug) => ['author', locale, slug],
    /**
     * The filters are part of the key, not just of the request: two searches for the same
     * words with different filters are different results, and leaving them out would serve
     * the reader the last filter's hits while the new ones load.
     */
    search: (locale, query, page, sort, filters = {}) => ['search', locale, query, page, sort, normalize(filters)],
    trending: (locale) => ['trending', locale],
    breaking: (locale) => ['breaking', locale],
    liveEvents: (locale) => ['live-events', locale],
    liveEvent: (locale, slug, page) => ['live-event', locale, slug, page],
    videos: (locale, category) => ['videos', locale, category ?? 'all'],
    video: (locale, slug) => ['video', locale, slug],
};
/**
 * Key equality in TanStack Query is structural, and `{ category: 'sports' }` is not the
 * same key as `{ category: 'sports', district: undefined }`. Dropping empty values means
 * the server and the client cannot disagree about a filter neither of them set.
 */
function normalize(filters) {
    return Object.fromEntries(Object.entries(filters)
        .filter((entry) => Boolean(entry[1]))
        .sort(([a], [b]) => a.localeCompare(b)));
}
//# sourceMappingURL=query-keys.js.map